import Image from "next/image";
import { CRYPTO_OPTIONS } from "@/config/crypto";
import { cn } from "@/lib/utils";

export const PAYMENT_ICON_MAP: Record<string, { src: string; alt: string; width?: number; height?: number }> =
	{
		card: { src: "/payment-icons/compact/visa.webp", alt: "Visa", width: 96, height: 96 },
		visa: { src: "/payment-icons/compact/visa.webp", alt: "Visa", width: 96, height: 96 },
		mastercard: { src: "/payment-icons/compact/mastercard.webp", alt: "Mastercard", width: 96, height: 96 },
		stripe: { src: "/payment-icons/stripe.svg", alt: "Stripe" },
		paypal: { src: "/payment-icons/compact/paypal.webp", alt: "PayPal", width: 96, height: 96 },
		"apple-pay": { src: "/payment-icons/compact/applepay.webp", alt: "Apple Pay", width: 96, height: 96 },
		"google-pay": { src: "/payment-icons/compact/googlepay.webp", alt: "Google Pay", width: 96, height: 96 },
		amex: { src: "/payment-icons/amex.svg", alt: "American Express" },
		sepa: { src: "/payment-icons/compact/sepa.webp", alt: "SEPA", width: 96, height: 96 },
		"revolut-pay": { src: "/payment-icons/compact/revolut.webp", alt: "Revolut", width: 96, height: 96 },
		"robinhood-connect": { src: "/payment-icons/robinhood-logo.webp", alt: "Robinhood" },
		banxa: { src: "/payment-icons/banxa.png", alt: "Banxa" },
		"coinbase-pay": { src: "/payment-icons/coinbase.svg", alt: "Coinbase Pay" },
		binance: { src: "/payment-icons/binance.png", alt: "Binance Pay" },
	};

const ALL_PAYMENT_ICONS = [
	PAYMENT_ICON_MAP.visa,
	PAYMENT_ICON_MAP.mastercard,
	PAYMENT_ICON_MAP.stripe,
	PAYMENT_ICON_MAP.paypal,
	PAYMENT_ICON_MAP["apple-pay"],
	PAYMENT_ICON_MAP["google-pay"],
	PAYMENT_ICON_MAP.amex,
	PAYMENT_ICON_MAP.sepa,
	PAYMENT_ICON_MAP["revolut-pay"],
] as const;

export const CRYPTO_PAYMENT_ICONS = CRYPTO_OPTIONS.map(({ icon, label }) => ({
	src: icon,
	alt: label,
})) as ReadonlyArray<{ src: string; alt: string }>;

interface PaymentIconsProps {
	label?: string;
	size?: "sm" | "md";
	className?: string;
}

export function PaymentIcons({ label, size = "sm", className }: PaymentIconsProps) {
	const imgClass = size === "sm" ? "h-5 w-auto" : "h-6 w-auto";
	const pillClass = size === "sm" ? "h-8 min-w-[2.75rem] px-2" : "h-10 min-w-[3rem] px-2.5";

	return (
		<div className={cn("flex flex-wrap items-center gap-2", className)}>
			{label && (
				<span className="font-mono text-[0.55rem] uppercase tracking-[0.2em] text-muted-foreground">
					{label}
				</span>
			)}
			<div className="flex flex-wrap items-center gap-1.5">
				{ALL_PAYMENT_ICONS.map((method) => (
					<div
						key={method.alt}
						title={method.alt}
						className={cn(
							"flex items-center justify-center rounded-md border border-border bg-white shadow-[0_2px_6px_-3px_rgba(8,39,74,0.18)] transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_12px_-4px_rgba(8,39,74,0.25)]",
							pillClass,
						)}
					>
						<Image
							src={method.src}
							alt={method.alt}
							width={method.width ?? 48}
							height={method.height ?? 32}
							className={cn("object-contain", imgClass)}
							unoptimized
						/>
					</div>
				))}
			</div>
		</div>
	);
}

interface PaymentMethodIconProps {
	methodId: string;
	className?: string;
}

export function PaymentMethodIcon({ methodId, className }: PaymentMethodIconProps) {
	const icon = PAYMENT_ICON_MAP[methodId];
	if (!icon) return null;

	return (
		<Image
			src={icon.src}
			alt={icon.alt}
			width={icon.width ?? 32}
			height={icon.height ?? 20}
			className={cn("h-5 w-auto rounded-sm object-contain", className)}
			unoptimized
		/>
	);
}
